// Generated by Claude Code
import { Api, MAX_PAGE_SIZE } from '#core/api/Api.ts'
import ApiUser from '#core/entities/auth/api-users/ApiUser.ts'
import ApiUserCreated from '#core/entities/auth/api-users/ApiUserCreated.ts'
import ApiUserSecret from '#core/entities/auth/api-users/ApiUserSecret.ts'
import type { Permission } from '#core/entities/auth/roles/Permission.ts'
import type { BaseIdModel } from '#core/entities/shared/BaseModels.ts'
import { QueryResult } from '#core/entities/shared/Query.ts'

/**
 * Company-scoped API users. Reads and secret rotation live under `auth/api-users`;
 * every other mutation is super-user-only under `auth/super_user/api-users`.
 */
export default class ApiUsersApi extends Api<ApiUsersApi> {
  constructor() {
    super()
  }

  async listApiUsers(take: number = MAX_PAGE_SIZE): Promise<ApiUser[]> {
    const result = await this.get<QueryResult<BaseIdModel>>('auth/api-users/', { take })
    return this.toQueryResult({ take }, result, ApiUser).results
  }

  async getApiUser(id: string): Promise<ApiUser | null> {
    return this.get<ApiUser>(`auth/api-users/${id}`, undefined, ApiUser)
  }

  /** Issues a new secret; the old one stops working for new logins immediately. */
  async rotateSecret(id: string): Promise<ApiUserSecret | null> {
    return this.post<ApiUserSecret>(`auth/api-users/${id}/rotate-secret`, undefined, ApiUserSecret)
  }

  /** Creates an API user holding either a role or personal permissions; the response carries the one-time secret. */
  async createApiUser(
    name: string,
    roleId: string | null,
    personalPermissions: Permission[] | null,
  ): Promise<ApiUserCreated | null> {
    return this.post<ApiUserCreated>(
      'auth/super_user/api-users/',
      { name, role_id: roleId, personal_permissions: personalPermissions },
      ApiUserCreated,
    )
  }

  async renameApiUser(id: string, name: string): Promise<ApiUser | null> {
    return this.patch<ApiUser>(`auth/super_user/api-users/${id}`, { name }, ApiUser)
  }

  async updateAssignment(
    id: string,
    roleId: string | null,
    personalPermissions: Permission[] | null,
  ): Promise<ApiUser | null> {
    return this.patch<ApiUser>(
      `auth/super_user/api-users/${id}/assignment`,
      { role_id: roleId, personal_permissions: personalPermissions },
      ApiUser,
    )
  }

  async setEnabled(id: string, enabled: boolean): Promise<ApiUser | null> {
    return this.patch<ApiUser>(`auth/super_user/api-users/${id}/enabled`, { enabled }, ApiUser)
  }

  async deleteApiUser(id: string): Promise<void> {
    await this.delete(`auth/super_user/api-users/${id}`)
  }
}
